import type { GoodsBasicResponse } from 'shared/src/api/goodsService/getGoodsBasic/types';
import { localizeNumber } from 'shared/src/utils';

const BASE_TITLE = '에이블리 스토어';

/**
 * 상품 기본 정보에서 스토어명을 추출합니다.
 */
const getStoreName = (goods: GoodsBasicResponse['goods']) => {
  return goods.brand?.name ?? goods.market.name;
};

/**
 * 상품 기본 정보에서 카테고리 정보를 추출합니다.
 */
const getCategoryName = (goods: GoodsBasicResponse['goods']) => {
  const firstCategory = goods.display_categories[0];
  if (firstCategory == null) return '';

  return firstCategory.parent_category != null
    ? `${firstCategory.parent_category.name}, ${firstCategory.name}`
    : firstCategory.name;
};

/**
 * 상품 기본 정보에서 공통 메타 데이터를 추출합니다.
 */
export const extractMetaData = (goods: GoodsBasicResponse['goods']) => {
  return {
    goodsName: goods.name,
    storeName: getStoreName(goods),
    category: getCategoryName(goods),
  };
};

export const getMetaTitle = (goodsBasic?: GoodsBasicResponse['goods']) => {
  if (goodsBasic == null) return BASE_TITLE;

  const { goodsName, storeName } = extractMetaData(goodsBasic);

  if (goodsName.includes(storeName) === true) {
    return `${goodsName} - ${BASE_TITLE}`;
  }

  return `${storeName} ${goodsName} - ${BASE_TITLE}`;
};

export const getMetaDescription = (
  goodsBasic?: GoodsBasicResponse['goods'],
) => {
  if (goodsBasic == null) return undefined;

  const { goodsName, storeName, category } = extractMetaData(goodsBasic);
  return `${storeName} ${goodsName} ${category}`;
};

export const getMetaKeywords = (goodsBasic?: GoodsBasicResponse['goods']) => {
  if (goodsBasic == null) return undefined;

  const { goodsName, storeName, category } = extractMetaData(goodsBasic);
  return `${storeName}, ${goodsName}, ${category}`;
};

export const getProductMeta = (goods?: GoodsBasicResponse['goods']) => {
  if (goods == null) return undefined;

  const availability: 'in stock' | 'out of stock' | undefined = (() => {
    switch (goods.sale_type) {
      case 'ON_SALE':
        return 'in stock';
      case 'SOLD_OUT':
        return 'out of stock';
    }
  })();

  return {
    brand: getStoreName(goods),
    priceCurrency: 'KRW',
    retailerItemId: String(goods.sno),
    priceAmount: localizeNumber(goods.price_info.thumbnail_price),
    availability,
  };
};
